import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { pixelToRem } from '@bluefox/constants';
import { BillingDashboardRecoveredTime } from '@graphql/billing';
import { Card, Dimmer, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import ChartErrorMessage from '@bluefox/ui/Dashboard/ChartErrorMessage';
import { CardContent, CardHeader } from '@bluefox/ui/Dashboard/CommonStyles';
import { sumHoursMinutesFromBE } from '@bluefox/lib/maths/hourMinutesFromBE';

type Props = {
  practiceIds: string[];
  dateText?: string;
  month?: number;
  year?: number;
};

const TimeHourValueParagraph = styled.p`
  font-size: ${pixelToRem(50)}rem !important;
  font-weight: 700 !important;
  line-height: 1.25 !important;
  text-align: center;
  margin: 2rem 0 0 !important;
`;
const MinuteValueParagraph = styled.p`
  font-size: ${pixelToRem(40)}rem !important;
  font-weight: 700 !important;
  line-height: 1.25 !important;
  text-align: center;
  margin: 2rem 0 0 !important;
`;
const TimeLegendParagraph = styled.p`
  font-size: ${pixelToRem(18)}rem !important;
  font-weight: 500 !important;
  line-height: 1.25 !important;
  text-align: center;
  margin: 0 0 !important;
`;

const TimeParagraph = styled.p`
  font-size: ${pixelToRem(14)}rem !important;
  font-weight: 500 !important;
  line-height: 1.25 !important;
  text-align: center;
  margin: 2rem 0 0 !important;
`;

type DataProps = {
  vaccinationsTime: number;
};

const TimeRestoredCard = ({ practiceIds, month, year, dateText }: Props) => {
  const { data, loading, error } = useQuery(BillingDashboardRecoveredTime, {
    variables: {
      practiceIds: practiceIds,
      month: month !== undefined ? month + 1 : undefined,
      year: year,
    },
    skip: !practiceIds || month === undefined || year === undefined,
  });

  const renderData = useCallback((data: DataProps[], dateTextCb?: string) => {
    const { hours: totalVaccinationsHours, minutes: totalVaccinationsMinutes } =
      sumHoursMinutesFromBE(data.map((item) => item.vaccinationsTime));

    return (
      <>
        <TimeHourValueParagraph>
          {totalVaccinationsHours}
        </TimeHourValueParagraph>
        <TimeLegendParagraph>Hours</TimeLegendParagraph>
        <MinuteValueParagraph>{totalVaccinationsMinutes}</MinuteValueParagraph>
        <TimeLegendParagraph>Minutes</TimeLegendParagraph>
        <TimeParagraph>
          By teaming up with Canid, you've saved{' '}
          <b>
            {totalVaccinationsHours} hours and {totalVaccinationsMinutes}{' '}
            minutes
          </b>
          {dateTextCb && (
            <>
              {' '}
              this <b>{dateTextCb}</b>
            </>
          )}
          , giving you the freedom to prioritize high-quality patient care.
        </TimeParagraph>
      </>
    );
  }, []);

  return (
    <Card fluid style={{ height: '100%' }}>
      <CardContent>
        <CardHeader>Time Restored</CardHeader>
        <Dimmer active={loading}>
          <Loader />
        </Dimmer>
        {error && (
          <ChartErrorMessage>
            <p>Error: {error?.message}</p>
          </ChartErrorMessage>
        )}
        {!loading &&
          !error &&
          data?.billing_dashboard_claims_by_status &&
          renderData(data?.billing_dashboard_claims_by_status, dateText)}
      </CardContent>
    </Card>
  );
};

export default TimeRestoredCard;
